import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import {
  workflowInsightsApi,
  INSIGHTS_PERIODS,
  type CompanyInsights,
  type InsightsPeriod,
  type WorkflowInsightRow,
} from "@/api/workflow-insights";
import { KpiStrip, type KpiItem } from "@/components/ui/kpi-strip";
import { Segmented } from "@/components/ui/segmented";
import { AgentAvatarStack } from "@/components/agent/agent-avatar";
import { withWorkspaceContext } from "@/lib/workspace-navigation";
import {
  formatCostCents,
  formatDateRange,
  formatDurationMs,
  formatHours,
  formatInt,
  formatPercent,
  formatRelativeTime,
  formatSignedPercent,
  formatTokens,
} from "@/lib/format";
import { RunsPerDayChart } from "./runs-per-day-chart";
import { triggerLabel } from "./trigger-label";

/**
 * Workflows › Insights (design 5a / 5c). Aggregated run metrics for a period
 * with a per-workflow table where every row drills in to that workflow.
 *
 * `period` lives in the URL (`?period=`) so a link to the tab carries its
 * window; the agent filter and sort are local.
 */
export function WorkflowInsightsView({ agentId: scopedAgentId }: { agentId: string | null }) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodParam = searchParams.get("period");
  const period: InsightsPeriod = (INSIGHTS_PERIODS as string[]).includes(periodParam ?? "")
    ? (periodParam as InsightsPeriod)
    : "30d";
  const setPeriod = (next: InsightsPeriod) => {
    const params = new URLSearchParams(searchParams);
    if (next === "30d") params.delete("period");
    else params.set("period", next);
    setSearchParams(params, { replace: true });
  };

  // The workspace's agent scope wins; otherwise the header dropdown filters.
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const effectiveAgentId = scopedAgentId ?? agentFilter;
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const agentById = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["workflow-insights", tenantId, period, effectiveAgentId ?? "all"],
    queryFn: () => workflowInsightsApi.company(tenantId, period, effectiveAgentId),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  type SortCol = "name" | "runs" | "successRate" | "avgDurationMs" | "tokens" | "lastRunAt";
  const [sort, setSort] = useState<{ col: SortCol; dir: "asc" | "desc" }>({ col: "runs", dir: "desc" });
  const toggleSort = (col: SortCol) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "name" ? "asc" : "desc" }));

  const rows = useMemo(() => {
    const list = [...(data?.workflows ?? [])];
    const val = (w: WorkflowInsightRow): number | string => {
      switch (sort.col) {
        case "name": return w.name.toLowerCase();
        case "runs": return w.runs;
        case "successRate": return w.successRate ?? -1;
        case "avgDurationMs": return w.avgDurationMs ?? -1;
        case "tokens": return w.tokensIn + w.tokensOut;
        case "lastRunAt": return w.lastRunAt ? new Date(w.lastRunAt).getTime() : 0;
      }
    };
    list.sort((a, b) => {
      const x = val(a), y = val(b);
      const cmp = typeof x === "string" && typeof y === "string" ? x.localeCompare(y) : (x as number) - (y as number);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [data?.workflows, sort]);

  const kpis = buildKpis(data, period);

  const exportCsv = () => {
    if (!data) return;
    const header = ["Workflow", "Trigger", "Agents", "Runs", "Completed", "Failed", "Success %", "Avg duration (s)", "Tokens in", "Tokens out", "Cost (USD)", "Last run"];
    const lines = rows.map((w) => [
      w.name,
      triggerLabel(w.trigger),
      w.agentIds.map((id) => agentById.get(id)?.name ?? id).join("; "),
      w.runs,
      w.completed,
      w.failed,
      w.successRate ?? "",
      w.avgDurationMs != null ? (w.avgDurationMs / 1000).toFixed(1) : "",
      w.tokensIn,
      w.tokensOut,
      w.priced ? (w.costCents / 100).toFixed(2) : "",
      w.lastRunAt ?? "",
    ]);
    const csv = [header, ...lines]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workflow-insights-${period}-${data.until.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openWorkflow = (id: string) =>
    navigate(withWorkspaceContext(`/tenants/${tenantId}/workflows/${id}/insights?period=${period}`, searchParams));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Controls row */}
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-b border-border px-6 py-[10px]">
        <Segmented
          aria-label="Period"
          value={period}
          onChange={setPeriod}
          options={INSIGHTS_PERIODS.map((p) => ({ value: p, label: p }))}
        />
        {!scopedAgentId && (
          <div className="relative">
            <select
              value={agentFilter ?? ""}
              onChange={(e) => setAgentFilter(e.target.value || null)}
              aria-label="Filter by agent"
              className="h-[26px] appearance-none rounded-md border border-border bg-transparent pl-[9px] pr-6 text-[12px] font-medium text-text-primary outline-none hover:bg-surface-hover focus:border-text-muted"
            >
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <ChevronDown size={12} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        )}
        <button
          type="button"
          onClick={exportCsv}
          disabled={!data}
          className="inline-flex h-[26px] items-center gap-1.5 rounded-md border border-border px-[9px] text-[12px] font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary disabled:opacity-50"
        >
          <Download size={12} />
          Export
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className={cn("mx-auto flex w-full max-w-6xl flex-col gap-[18px] px-6 pt-[18px] pb-8 transition-opacity", isFetching && !isLoading && "opacity-80")}>
          <KpiStrip items={kpis} loading={isLoading && !data} />

          {/* Runs per day */}
          <section>
            <div className="mb-2 flex items-center gap-3">
              <h2 className="text-[12.5px] font-semibold text-text-primary">Runs per day</h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
                <span className="h-2 w-2 rounded-[2px] bg-[#10a37f]" /> Completed
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
                <span className="h-2 w-2 rounded-[2px] bg-[#dc2626] dark:bg-[#ef4444]" /> Failed
              </span>
              <span className="ml-auto text-[11px] text-text-muted">
                {data ? formatDateRange(data.since, data.until) : ""}
              </span>
            </div>
            <RunsPerDayChart points={data?.perDay ?? []} loading={isLoading && !data} />
          </section>

          {/* By workflow */}
          <section>
            <h2 className="mb-2 text-[12.5px] font-semibold text-text-primary">By workflow</h2>
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <div
                  className="grid items-center gap-3 border-b border-border px-[10px] py-[6px] text-[11px] font-medium text-text-muted"
                  style={{ gridTemplateColumns: GRID }}
                >
                  <SortHeader label="Workflow" col="name" sort={sort} onSort={toggleSort} />
                  <span>Trigger</span>
                  <span>Agents</span>
                  <SortHeader label="Runs" col="runs" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Success" col="successRate" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Avg duration" col="avgDurationMs" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Tokens" col="tokens" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Last run" col="lastRunAt" sort={sort} onSort={toggleSort} align="right" />
                </div>
                {isLoading && !data
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="grid items-center gap-3 border-b border-[#f0f0ee] px-[10px] py-2 dark:border-[#1a1b1e]" style={{ gridTemplateColumns: GRID }}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <div key={j} className="h-3 animate-pulse rounded bg-surface-hover" />
                        ))}
                      </div>
                    ))
                  : rows.length === 0
                    ? <p className="px-[10px] py-6 text-[12px] text-text-muted">No workflows{effectiveAgentId ? " for this agent" : ""} yet.</p>
                    : rows.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => openWorkflow(w.id)}
                          className="grid w-full cursor-pointer items-center gap-3 border-b border-[#f0f0ee] px-[10px] py-2 text-left transition-colors hover:rounded-md hover:bg-sidebar dark:border-[#1a1b1e]"
                          style={{ gridTemplateColumns: GRID }}
                        >
                          <span className="truncate whitespace-nowrap text-[12.5px] font-medium text-text-primary">{w.name}</span>
                          <span className="truncate whitespace-nowrap text-[12px] text-text-secondary">{triggerLabel(w.trigger)}</span>
                          <span className="min-w-0">
                            {w.agentIds.length > 0 ? (
                              <AgentAvatarStack
                                agents={w.agentIds.map((id) => agentById.get(id) ?? { id, name: "?" })}
                                size={18}
                              />
                            ) : (
                              <span className="text-[11px] text-text-muted">—</span>
                            )}
                          </span>
                          <span className="text-right text-[12.5px] tabular-nums text-text-primary">{formatInt(w.runs)}</span>
                          <SuccessBar rate={w.successRate} />
                          <span className="text-right text-[12px] tabular-nums text-text-secondary">{formatDurationMs(w.avgDurationMs)}</span>
                          <span className="text-right text-[12px] tabular-nums text-text-secondary" title={w.tokensIn + w.tokensOut > 0 ? `${formatTokens(w.tokensIn)} in · ${formatTokens(w.tokensOut)} out` : "No token telemetry for these runs"}>
                            {w.tokensIn + w.tokensOut > 0 ? formatTokens(w.tokensIn + w.tokensOut) : "—"}
                          </span>
                          <span className="text-right text-[11.5px] tabular-nums text-text-muted">{formatRelativeTime(w.lastRunAt)}</span>
                        </button>
                      ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

const GRID = "1.5fr 1.35fr 60px 54px 104px 88px 60px 66px";

function buildKpis(data: CompanyInsights | undefined, period: InsightsPeriod): KpiItem[] {
  const t = data?.totals;
  const topReason = data?.failureReasons?.[0];
  const tokensTotal = (t?.tokensIn ?? 0) + (t?.tokensOut ?? 0);
  return [
    {
      label: "Runs",
      value: formatInt(t?.runs),
      sub: t?.runsChangePct != null ? `${formatSignedPercent(t.runsChangePct)} vs prior ${period}` : t ? `${formatInt(t.priorRuns)} in prior ${period}` : null,
      subTone: t?.runsChangePct != null && t.runsChangePct >= 0 ? "success" : "muted",
    },
    {
      label: "Completed",
      value: formatPercent(t?.successRate),
      sub: t ? `${formatInt(t.completed)} runs` : null,
    },
    {
      label: "Failed",
      value: formatInt(t?.failed),
      sub: topReason && topReason.kind !== "cancelled" ? `${formatInt(topReason.count)} ${topReason.label}` : t && t.cancelled > 0 ? `${formatInt(t.cancelled)} cancelled` : t ? "no failures" : null,
      subTone: t && t.failed > 0 ? "danger" : "muted",
    },
    {
      label: "Agent hours",
      value: formatHours(t?.durationMsTotal),
      sub: t?.avgDurationMs != null ? `avg ${formatDurationMs(t.avgDurationMs)} per run` : null,
    },
    {
      label: "Tokens",
      value: tokensTotal > 0 ? formatTokens(tokensTotal) : t ? "—" : "",
      sub: tokensTotal > 0 ? `${formatTokens(t!.tokensIn)} in · ${formatTokens(t!.tokensOut)} out` : t ? "not tracked for these runs" : null,
      title: tokensTotal === 0 ? "No telemetry rows attributed to workflow runs in this period" : undefined,
    },
    {
      label: "Cost",
      value: t?.priced ? formatCostCents(t.costCents) : t ? "—" : "",
      sub: t?.costPerRunCents != null ? `${formatCostCents(t.costPerRunCents)} per run` : t ? "no priced usage" : null,
    },
  ];
}

function SortHeader<T extends string>({
  label,
  col,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  col: T;
  sort: { col: T; dir: "asc" | "desc" };
  onSort: (col: T) => void;
  align?: "left" | "right";
}) {
  const active = sort.col === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap font-medium transition-colors hover:text-text-primary",
        align === "right" && "justify-end",
        active && "text-text-primary",
      )}
    >
      {label}
      {active && (sort.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
    </button>
  );
}

export function SuccessBar({ rate }: { rate: number | null }) {
  if (rate == null) return <span className="text-[11.5px] text-text-muted">—</span>;
  const tone = rate < 75 ? "bg-[#dc2626] dark:bg-[#ef4444]" : rate < 90 ? "bg-[#d97706] dark:bg-[#f59e0b]" : "bg-[#10a37f]";
  const text = rate < 75 ? "text-[#dc2626] dark:text-[#ef4444]" : rate < 90 ? "text-[#d97706] dark:text-[#f59e0b]" : "text-text-primary";
  return (
    <span className="flex items-center gap-2">
      <span className="h-1 flex-1 overflow-hidden rounded-[2px] bg-border">
        <span className={cn("block h-full rounded-[2px] transition-[width] duration-200", tone)} style={{ width: `${Math.max(2, Math.min(100, rate))}%` }} />
      </span>
      <span className={cn("min-w-[38px] text-right text-[11.5px] tabular-nums", text)}>{formatPercent(rate)}</span>
    </span>
  );
}
